import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { NOT_FOUND_HEAD_FALLBACK } from "@/lib/brandIcons";
import { blogTermHead } from "@/lib/blog/seo";
import { TermPage } from "#/components/blog/TermPage";

// See the list route: cache only a 200, never a notFound()/404 — an unknown
// category slug must not be edge-cached as missing.
const CACHE = "public, s-maxage=60, stale-while-revalidate=86400";

const getCategoryPosts = createServerFn({ method: "GET" })
  .validator((category: string) => category)
  .handler(async ({ data: category }) => {
    const { loadBlogTerm, resolveDogfoodWorkspaceId } = await import(
      "@/lib/blog/queries"
    );
    const workspaceId = resolveDogfoodWorkspaceId();
    return workspaceId
      ? await loadBlogTerm(workspaceId, "categories", category)
      : undefined;
  });

export const Route = createFileRoute("/blog/categories/$category")({
  ssr: true,
  loader: async ({ params }) => {
    const data = await getCategoryPosts({ data: params.category });
    if (!data) throw notFound();
    return data;
  },
  headers: ({ loaderData }) =>
    loaderData ? { "cache-control": CACHE } : { "cache-control": "no-store" },
  // notFound()-branch icon fallback + the `as never` cast it needs: see
  // `NOT_FOUND_HEAD_FALLBACK`'s docblock (#1099 / US2).
  head: ({ loaderData }) =>
    (loaderData
      ? blogTermHead(
          loaderData.blog,
          loaderData.kind,
          loaderData.term,
          loaderData.canonical,
          loaderData.feedUrl,
        )
      : NOT_FOUND_HEAD_FALLBACK) as never,
  component: BlogCategoryPage,
});

function BlogCategoryPage() {
  const { kind, term, posts } = Route.useLoaderData();
  return <TermPage kind={kind} term={term} posts={posts} />;
}
